import { getBaseRole } from '@/helpers/customRoles';
import { FC, ReactNode, useRef, useState } from 'react'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { Spin } from 'antd'
import Button from '@UI/New/Button'
import { usersAPI } from '@/api'
import rolesEnum from '@/constant/rolesEnum'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import {
  getStudentProfileFields,
  isStudentProfileFieldVisibleForRole,
} from '@/helpers/studentProfileFields'
import {
  findUserDocumentByFileId,
  getComplianceDocumentStateView,
  getRequiredDocumentDisplayName,
  isRequiredDocumentUploaded,
} from '@/helpers/new/requiredDocumentsCompliance.utils'
import type {
  ComplianceDocumentStateTone,
  UserDocument,
} from '@/helpers/new/requiredDocumentsCompliance.utils'
import parentCn from '../../index.module.css'
import cn from './index.module.css'
import AddContractDropdown from '../../../AddContractModal/AddContractDropdown'
import StudentDocksList from './StudentDocksList'
import { IStudentDepartmentsAndContractsTabProps } from './studentContracts.types'

const ContainerWithHeader: FC<{
  title: string
  children: ReactNode
  sectionClassName?: string
}> = ({ title, children, sectionClassName }) => (
  <div
    className={classNames(parentCn.containerWithHeader, sectionClassName)}
  >
    <div className={parentCn.containerWithHeaderTitle}>{title}</div>
    {children}
  </div>
)

const StudentDepartmentsAndContractsTab: FC<
  IStudentDepartmentsAndContractsTabProps
> = ({
  platformSettings = {},
  customProfileFields = [],
  student = {},
  setModal = () => {},
  getUser = () => {},
  departmentsSection,
}) => {
  const profile = useSelector(selectProfile)
  const addAlert = useAddAlert()
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(
    null,
  )
  const [isRequiredDocumentsRefreshing, setIsRequiredDocumentsRefreshing] =
    useState(false)
  const showAgreements = getStudentProfileFields(
    platformSettings,
    customProfileFields,
  )
    .filter((field) => isStudentProfileFieldVisibleForRole(field, profile?.role))
    .some((field) => field.id === 'agreements')
  const studentRequiredDocuments =
    student?.documents_compliance?.required_documents
  const requiredComplianceDocuments = Array.isArray(studentRequiredDocuments)
    ? studentRequiredDocuments
    : []
  const studentDocuments = (student?.documents ?? []) as UserDocument[]
  const canUploadRequiredDocuments = [
    rolesEnum.admin,
    rolesEnum.juniorAdmin,
  ].includes(getBaseRole(profile?.role))
  const showRequiredDocuments =
    canUploadRequiredDocuments && requiredComplianceDocuments.length > 0

  const getRequiredDocumentStateClass = (
    tone: ComplianceDocumentStateTone,
  ) => {
    switch (tone) {
      case 'approved':
        return cn.requiredDocumentStateApproved
      case 'rejected':
        return cn.requiredDocumentStateRejected
      case 'pending':
        return cn.requiredDocumentStatePending
      case 'selected':
        return cn.requiredDocumentStateSelected
      default:
        return cn.requiredDocumentStateNotUploaded
    }
  }

  const handleRequiredDocumentUpload = async (
    document: (typeof requiredComplianceDocuments)[number],
    file?: File,
  ) => {
    if (!file || !student?.id) return

    const documentKey = String(document?.id ?? document?.name ?? '')
    const documentName = String(document?.name ?? '').trim()
    const formData = new FormData()

    formData.append(
      'message',
      `Загрузка обязательного документа: ${documentName || 'Документ'}`,
    )
    formData.append('document_name', documentName || 'Документ')
    formData.append('user_id', String(student.id))
    formData.append('author_type', 'employee')
    if (document?.id) {
      formData.append('required_document_id', String(document.id))
    }
    formData.append('files', file)

    setUploadingDocumentId(documentKey)

    try {
      await usersAPI.sendDocumentForVerification(formData)
      addAlert({ title: 'Документ отправлен на проверку' })
      setUploadingDocumentId(null)
      setIsRequiredDocumentsRefreshing(true)
      try {
        await Promise.resolve(getUser(false))
      } catch {}
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось отправить документ. Попробуйте ещё раз.',
        type: 'error',
      })
    } finally {
      setUploadingDocumentId(null)
      setIsRequiredDocumentsRefreshing(false)
    }
  }

  return (
    <div className={classNames(parentCn.bodyContent, cn.departmentsContractsTab)}>
      <ContainerWithHeader
        title="Отделы"
        sectionClassName={cn.contractsSection}
      >
        {departmentsSection}
      </ContainerWithHeader>
      {showAgreements && (
        <ContainerWithHeader
          title="Договоры"
          sectionClassName={cn.contractsSection}
        >
          <AddContractDropdown
            className={classNames(
              parentCn.secondaryButton,
              parentCn.textSemiBold,
            )}
            onAddFile={() => setModal({ type: 'AddContract' })}
            onAddTemplate={() => setModal({ type: 'AddTemplateContract' })}
          />
          <StudentDocksList
            student={student}
            onContractChanged={() => getUser(false)}
            onDeleteDock={(dock) =>
              dock.typeDocument === 'contract'
                ? setModal({ type: 'RemoveContract', data: dock?.id })
                : setModal({ type: 'RemoveDocument', data: dock?.idTwo })
            }
          />
        </ContainerWithHeader>
      )}
      {showRequiredDocuments && (
        <ContainerWithHeader
          title="Обязательные документы"
          sectionClassName={cn.contractsSection}
        >
          <div className={cn.requiredDocumentsListShell}>
            {isRequiredDocumentsRefreshing && (
              <div className={cn.requiredDocumentsBusyOverlay} aria-busy>
                <Spin size="small" />
              </div>
            )}
            <div className={cn.requiredDocumentsList}>
              {requiredComplianceDocuments.map((document) => {
                const documentKey = String(document?.id ?? document?.name)
                const isUploaded = isRequiredDocumentUploaded(document)
                const matchedFile = findUserDocumentByFileId(
                  studentDocuments,
                  document?.file_id,
                )
                const fileLink = matchedFile?.path
                  ? getCorrectFilePath(matchedFile.path)
                  : ''
                const stateView = getComplianceDocumentStateView(document)
                const documentTitle = getRequiredDocumentDisplayName(
                  document,
                  studentDocuments,
                )
                const canOpenFile =
                  Boolean(fileLink) &&
                  document?.state !== 'rejected' &&
                  Boolean(document?.is_uploaded)
                const isUploading = uploadingDocumentId === documentKey

                return (
                  <div key={documentKey} className={cn.requiredDocumentCard}>
                    <span
                      className={cn.requiredDocumentName}
                      title={documentTitle}
                    >
                      {documentTitle}
                    </span>
                    <span
                      className={classNames(
                        cn.requiredDocumentBadge,
                        getRequiredDocumentStateClass(stateView.tone),
                      )}
                    >
                      {stateView.label}
                    </span>
                    <input
                      ref={(element) => {
                        fileInputRefs.current[documentKey] = element
                      }}
                      type="file"
                      className={cn.hiddenInput}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        event.currentTarget.value = ''
                        void handleRequiredDocumentUpload(document, file)
                      }}
                    />
                    {canOpenFile ? (
                      <a
                        className={cn.requiredDocumentAction}
                        href={fileLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button color="gray">Файл</Button>
                      </a>
                    ) : (
                      <Button
                        color={isUploaded ? 'gray' : 'primary'}
                        disabled={isUploading}
                        isLoading={isUploading}
                        className={cn.requiredDocumentAction}
                        onClick={() =>
                          fileInputRefs.current[documentKey]?.click()
                        }
                      >
                        {isUploaded ? 'Загрузить другой файл' : 'Загрузить'}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </ContainerWithHeader>
      )}
    </div>
  )
}

export default StudentDepartmentsAndContractsTab
